import PropTypes from 'prop-types'
import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'

import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { adjuntoBuilder } from '../reutilizables/upload/adjuntoBuilder.js'
import * as categoriaService from '../servicios/categoriasService'
import * as publicacionesService from '../servicios/publicacionesService'
import { buscarTodosPorIdCategoria } from '../servicios/subcategoriaService'
import Select from '../ui/Select'
import styles from './IndicadorCategorias.module.css'

const idCategoriaServicios = 1540

const IndicarCategorias = ({
  onChange,
  esScrap,
  esMasiva,
  value,
  idUsuario,
  esServicio,
  categoriaFija,
  categoriasExcluidas
}) => {
  const [combosCategoria, setCombosCategoria] = useState([])
  const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState([])
  const [categoriasIniciales, setCategoriasIniciales] = useState([])
  const [botonHabilitado, setBotonHabilitado] = useState(false)
  const [loading, setLoading] = useState(false)

  const obtenerCategoriasBase = async (idCategoria) => {
    return await buscarTodosPorIdCategoria(idCategoria, esScrap, '', false)
  }

  const obtenerCategorias = async () => {
    if (esServicio) {
      const { data } = await obtenerCategoriasBase(idCategoriaServicios)
      setCategoriasIniciales(data)
    }
    const { data } = await obtenerCategoriasBase(0)
    const categoriasDisponibles = categoriasExcluidas?.length
      ? data.filter((cat) => !categoriasExcluidas.includes(Number(cat.idSubCategoria)))
      : data

    setCategoriasIniciales(categoriasDisponibles)

    if (categoriaFija) {
      const { data: subcategorias } = await obtenerCategoriasBase(categoriaFija)
      setCombosCategoria([
        {
          pos: 0,
          idCategoria: categoriaFija,
          bloqueado: true,
          categorias: data.filter((cat) => Number(cat.idSubCategoria) === categoriaFija)
        },
        {
          pos: 1,
          idCategoria: 0,
          categorias: [{ idSubCategoria: 0, descripcion: 'Elegir Subcategoría' }, ...subcategorias]
        }
      ])
      return
    }

    setCombosCategoria([
      {
        pos: 0,
        idCategoria: 0,
        categorias: [
          { idSubCategoria: 0, descripcion: 'Elegir Categoría' },
          ...categoriasDisponibles
        ]
      }
    ])
  }

  const obtenerRutaCategoria = useCallback(async () => {
    try {
      if (value === 0 || !value) {
        return
      }

      const { data } = await categoriaService.buscarPorId(value)
      const categoria = data?.[0]

      if (!categoria?.root) {
        return
      }

      const path = JSON.parse(categoria.root)

      const idsPath = path.map((item) => Number(item.id)).filter((id) => id > 0)

      if (idsPath.length === 0) {
        return
      }

      const combosReconstruidos = await idsPath.reduce(async (accPromise, idCategoria, i) => {
        const acc = await accPromise

        const idPadre = i === 0 ? 0 : idsPath[i - 1]

        const { data: categoriasNivel } = await obtenerCategoriasBase(idPadre)

        const esRaizBloqueada = i === 0 && Boolean(categoriaFija)
        const esRaizConExclusion = i === 0 && !esRaizBloqueada && categoriasExcluidas?.length

        const categoriasNivelFiltradas = esRaizConExclusion
          ? categoriasNivel.filter(
              (cat) => !categoriasExcluidas.includes(Number(cat.idSubCategoria))
            )
          : categoriasNivel

        acc.push({
          pos: i,
          idCategoria,
          bloqueado: esRaizBloqueada,
          categorias: esRaizBloqueada
            ? categoriasNivelFiltradas.filter((cat) => Number(cat.idSubCategoria) === categoriaFija)
            : [
                {
                  idSubCategoria: 0,
                  descripcion: i === 0 ? 'Elegir Categoría' : 'Elegir Subcategoría'
                },
                ...categoriasNivelFiltradas
              ]
        })

        return acc
      }, Promise.resolve([]))

      setCombosCategoria(combosReconstruidos)
    } catch {
      toast.error('Error al cargar la categoría seleccionada. Por favor, inténtalo de nuevo.')
    }
  }, [value, esScrap, categoriaFija, categoriasExcluidas])

  useEffect(() => {
    obtenerCategorias()
  }, [esScrap, categoriaFija, categoriasExcluidas])

  useEffect(() => {
    obtenerRutaCategoria()
  }, [obtenerRutaCategoria])

  const handleChange = async (event, pos) => {
    event.persist()
    const { value, options, selectedIndex } = event.target
    const idCategoria = value
    const selectedText = options[selectedIndex].text
    const { data: categorias } = await obtenerCategoriasBase(idCategoria)

    // si cambia de categoría "padre" elimina combos hijos para forzar elija de nuevo
    let combosFiltrado = combosCategoria.slice(0, pos + 1)

    // ya que eligió subcategoría elimina la opción "Elegir Subcategoría"
    combosFiltrado = combosFiltrado.map((cmb, i) => {
      return {
        ...cmb,
        idCategoria: i === pos ? idCategoria : cmb.idCategoria,
        categorias: cmb.categorias.filter((cat) => cat.idSubCategoria !== 0)
      }
    })

    if (categorias.length > 0) {
      onChange(
        {
          currentTarget: {
            ...event.target,
            name: 'idCategoria',
            value: 0
          }
        },
        'num'
      )

      setCombosCategoria([
        ...combosFiltrado,
        {
          pos: combosFiltrado.length,
          idCategoria,
          categorias: [{ idSubCategoria: 0, descripcion: 'Elegir Subcategoría' }, ...categorias]
        }
      ])
      setBotonHabilitado(false)
    } else {
      setCombosCategoria(combosFiltrado)
      onChange(
        {
          currentTarget: {
            ...event.target,
            name: 'idCategoria',
            value: idCategoria
          }
        },
        'num'
      )
      setCategoriasSeleccionadas((prev) => {
        if (prev.some((c) => c.idCategoria === idCategoria)) {
          return prev
        }
        return [...prev, { idCategoria, selectedText }]
      })
      setBotonHabilitado(true)
    }
  }

  const handleSeleccionarSiguiente = () => {
    if (categoriaFija) {
      obtenerCategorias()
      setBotonHabilitado(false)
      return
    }

    setCombosCategoria([
      {
        pos: 0,
        idCategoria: 0,
        categorias: [{ idSubCategoria: 0, descripcion: 'Elegir Categoría' }, ...categoriasIniciales]
      }
    ])
    setBotonHabilitado(false)
  }

  const handleGenerarExcel = async () => {
    try {
      setLoading(true)
      const { data } = await publicacionesService.generarExcelCargaMasiva({
        categoriasSeleccionadas,
        idVendedor: idUsuario
      })

      await adjuntoBuilder(data, null, 'Plantilla_Publicaciones', {
        isBase64: false
      })
    } catch (error) {
      toastErrorLog(
        error,
        'Ocurrió un error al generar el excel. Por favor, inténtalo de nuevo.',
        'cargaMasiva'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <p className="descripcion-cuadro-publicacion">
        Seleccionar bien la categoría hará que más interesados puedan encontrar tu producto.
      </p>
      <div>
        {combosCategoria.map((cat, i) => (
          <Select
            key={`${cat.idCategoria}-${cat.pos}`}
            // importante, para que setee idCategoria de componente "Publicar.jsx" recién cuando seleccione la categoría del último combo de categorías
            name={
              i === combosCategoria.length - 1 && !cat.bloqueado
                ? 'idCategoria'
                : `idcategoria${cat.idCategoria}`
            }
            value={cat.idCategoria}
            onChange={(e) => handleChange(e, cat.pos)}
            className="select-publicacion categorias-publicacion"
            datos={cat.categorias}
            disabled={cat.bloqueado}
            optionMetaDatos={{ value: 'idSubCategoria', text: 'descripcion' }}
          />
        ))}
      </div>
      {esMasiva && (
        <>
          <div style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!botonHabilitado || categoriasSeleccionadas.length === 0}
              onClick={handleSeleccionarSiguiente}
            >
              Elegir siguiente categoría
            </button>
            <button
              type="button"
              className="btn btn-primary ml-3"
              disabled={categoriasSeleccionadas.length === 0 || loading}
              onClick={handleGenerarExcel}
            >
              Generar excel
              {loading ? (
                <i className="fa fa-spinner fa-spin ml-2" />
              ) : (
                <i className="fa fa-file-excel-o ml-2" />
              )}
            </button>
          </div>
          {categoriasSeleccionadas.length > 0 && (
            <div className={styles.categoriasContainer}>
              <span style={{ fontWeight: 'bold' }}>Categorías seleccionadas:</span>
              {categoriasSeleccionadas.map(({ idCategoria, selectedText }) => (
                <div
                  key={idCategoria}
                  style={{
                    padding: '0.75rem 2.5rem 0.75rem 1rem',
                    background: '#f1f1f1',
                    borderRadius: '8px',
                    display: 'inline-block',
                    position: 'relative'
                  }}
                >
                  <span>
                    {idCategoria} - {selectedText}
                  </span>
                  <button
                    type="button"
                    aria-label="Quitar categoría"
                    onClick={() => {
                      const nuevasCategorias = categoriasSeleccionadas.filter(
                        (id) => id.idCategoria !== idCategoria
                      )
                      setCategoriasSeleccionadas(nuevasCategorias)
                    }}
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  )
}

IndicarCategorias.propTypes = {
  onChange: PropTypes.func.isRequired,
  esScrap: PropTypes.bool,
  esMasiva: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idUsuario: PropTypes.number,
  esServicio: PropTypes.bool,
  categoriaFija: PropTypes.number,
  categoriasExcluidas: PropTypes.arrayOf(PropTypes.number)
}

export default IndicarCategorias
